import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Stack,
  FormControlLabel,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Alert,
  Button,
} from '@mui/material';
import {
  NotificationsOutlined,
  PaletteOutlined,
  LanguageOutlined,
  SecurityOutlined,
  StorageOutlined,
} from '@mui/icons-material';

const Einstellungen = () => {
  const navigate = useNavigate();
  // Theme Settings
  const [darkMode, setDarkMode] = useState(false);
  const [compactView, setCompactView] = useState(false);
  
  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [taskReminders, setTaskReminders] = useState(true);
  const [paymentAlerts, setPaymentAlerts] = useState(true);
  
  // Language & Region
  const [language, setLanguage] = useState('de');
  const [dateFormat, setDateFormat] = useState('DD.MM.YYYY');
  const [currency, setCurrency] = useState('EUR');
  
  // Privacy Settings
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [dataSharingEnabled, setDataSharingEnabled] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSaveSettings = () => {
    // Hier würde normalerweise ein API-Call erfolgen
    localStorage.setItem('appSettings', JSON.stringify({
      darkMode,
      compactView,
      emailNotifications,
      pushNotifications,
      taskReminders,
      paymentAlerts,
      language,
      dateFormat,
      currency,
      analyticsEnabled,
      dataSharingEnabled,
    }));
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2200);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Einstellungen
      </Typography>

      {saved ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          Einstellungen wurden gespeichert.
        </Alert>
      ) : null}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Team & Betriebsfunktionen
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button variant="outlined" onClick={() => navigate('/mandanten/einladungen')}>Team & Rechte</Button>
          <Button variant="outlined" onClick={() => navigate('/einstellungen/abo')}>Abo & Features</Button>
        </Stack>
      </Paper>

      {/* Darstellung */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PaletteOutlined sx={{ mr: 1 }} />
          Darstellung
        </Typography>
        
        <FormControlLabel
          control={
            <Switch 
              checked={darkMode} 
              onChange={(e) => setDarkMode(e.target.checked)}
              disabled
            />
          }
          label="Dark Mode (in Entwicklung)"
          sx={{ mb: 2, display: 'block' }}
        />
        
        <FormControlLabel
          control={
            <Switch 
              checked={compactView} 
              onChange={(e) => setCompactView(e.target.checked)}
            />
          }
          label="Kompakte Ansicht"
          sx={{ display: 'block' }}
        />
        
        <Alert severity="info" sx={{ mt: 2 }}>
          Die kompakte Ansicht reduziert Abstände und zeigt mehr Informationen auf dem Bildschirm.
        </Alert>
      </Paper>

      {/* Benachrichtigungen */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <NotificationsOutlined sx={{ mr: 1 }} />
          Benachrichtigungen
        </Typography>
        
        <FormControlLabel
          control={
            <Switch 
              checked={emailNotifications} 
              onChange={(e) => setEmailNotifications(e.target.checked)}
            />
          }
          label="E-Mail-Benachrichtigungen"
          sx={{ mb: 2, display: 'block' }}
        />
        
        <FormControlLabel
          control={
            <Switch 
              checked={pushNotifications} 
              onChange={(e) => setPushNotifications(e.target.checked)}
              disabled
            />
          }
          label="Push-Benachrichtigungen (in Entwicklung)"
          sx={{ mb: 2, display: 'block' }}
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Benachrichtigungstypen:
        </Typography>
        
        <FormControlLabel
          control={
            <Switch 
              checked={taskReminders} 
              onChange={(e) => setTaskReminders(e.target.checked)}
            />
          }
          label="Aufgaben-Erinnerungen"
          sx={{ mb: 1, display: 'block' }}
        />
        
        <FormControlLabel
          control={
            <Switch 
              checked={paymentAlerts} 
              onChange={(e) => setPaymentAlerts(e.target.checked)}
            />
          }
          label="Zahlungserinnerungen"
          sx={{ display: 'block' }}
        />
      </Paper>

      {/* Sprache und Region */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LanguageOutlined sx={{ mr: 1 }} />
          Sprache und Region
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Sprache</InputLabel>
          <Select
            value={language}
            label="Sprache"
            onChange={(e) => setLanguage(e.target.value)}
          >
            <MenuItem value="de">Deutsch</MenuItem>
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="fr">Français</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Datumsformat</InputLabel>
          <Select
            value={dateFormat}
            label="Datumsformat"
            onChange={(e) => setDateFormat(e.target.value)}
          >
            <MenuItem value="DD.MM.YYYY">DD.MM.YYYY (31.12.2025)</MenuItem>
            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</MenuItem>
            <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl fullWidth>
          <InputLabel>Währung</InputLabel>
          <Select
            value={currency}
            label="Währung"
            onChange={(e) => setCurrency(e.target.value)}
          >
            <MenuItem value="EUR">EUR (€)</MenuItem>
            <MenuItem value="USD">USD ($)</MenuItem>
            <MenuItem value="GBP">GBP (£)</MenuItem>
            <MenuItem value="CHF">CHF (Fr.)</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Datenschutz & Sicherheit */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SecurityOutlined sx={{ mr: 1 }} />
          Datenschutz & Sicherheit
        </Typography>
        
        <FormControlLabel
          control={
            <Switch 
              checked={analyticsEnabled} 
              onChange={(e) => setAnalyticsEnabled(e.target.checked)}
            />
          }
          label="Anonyme Nutzungsstatistiken"
          sx={{ mb: 2, display: 'block' }}
        />
        
        <FormControlLabel
          control={
            <Switch 
              checked={dataSharingEnabled} 
              onChange={(e) => setDataSharingEnabled(e.target.checked)}
            />
          }
          label="Daten mit Partnern teilen"
          sx={{ mb: 2, display: 'block' }}
        />
        
        <Alert severity="info" sx={{ mt: 2 }}>
          Wir respektieren Ihre Privatsphäre. Alle Daten werden gemäß DSGVO verarbeitet.
        </Alert>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Sicherheitscenter
        </Typography>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Passwort & Kontozugriff
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Passwort vergessen oder kompromittiert? Nutzen Sie den Passwort-Reset über die Login-Seite.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/password-reset')}>
                Passwort zurücksetzen
              </Button>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Zugriffsmodell Mitarbeiter
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Mitarbeiterzugänge werden über Mandanten-Rollen gesteuert. Für reine Zeiterfassung ist die Rolle zeiterfasser vorgesehen.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/mandanten/einladungen')}>
                Team & Rechte öffnen
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Integrationen & Abrechnung
        </Typography>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                DATEV & Export
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Prüfen Sie regelmäßig, ob alle exportrelevanten Felder vollständig gepflegt sind, bevor Monatsabschlüsse erzeugt werden.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/dashboard/zeiterfassung')}>
                Zur Zeiterfassung
              </Button>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Abo, Limits und Features
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sehen Sie den aktuellen Planstatus und welche Features für Ihren Mandanten freigeschaltet sind.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/einstellungen/abo')}>
                Abo & Features öffnen
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </Paper>

      {/* Datenverwaltung */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StorageOutlined sx={{ mr: 1 }} />
          Datenverwaltung
        </Typography>
        
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Datensicherung
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Erstellen Sie eine Sicherungskopie Ihrer Daten.
            </Typography>
            <Button variant="outlined" disabled>
              Backup erstellen (in Entwicklung)
            </Button>
          </CardContent>
        </Card>
        
        <Card variant="outlined" sx={{ borderColor: '#e53e3e' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#e53e3e' }}>
              Konto löschen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Löschen Sie Ihr Konto und alle zugehörigen Daten permanent.
            </Typography>
            <Button 
              variant="outlined" 
              color="error"
              onClick={() => {
                if (window.confirm('Sind Sie sicher, dass Sie Ihr Konto löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
                  alert('Kontolöschung wird in einer zukünftigen Version implementiert.');
                }
              }}
            >
              Konto löschen
            </Button>
          </CardContent>
        </Card>
      </Paper>

      {/* Speichern Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => window.location.reload()}
        >
          Abbrechen
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSaveSettings}
          sx={{ 
            bgcolor: '#1a365d',
            '&:hover': { bgcolor: '#2d3748' }
          }}
        >
          Einstellungen speichern
        </Button>
      </Box>
    </Box>
  );
};

export default Einstellungen;
